/**
 * Inquiry Management Actions Component
 * Provides role-based action menus for inquiry management with 3-dot dropdown
 * Only includes View action
 */

import { useState, useRef, useEffect } from 'react';
import { Button } from '../ui/button';

interface InquiryManagementActionsProps {
  inquiryId: string;
  inquiryName: string;
  userRole?: string;
  onView?: () => void;
  onRefresh?: () => void;
}

export function InquiryManagementActions({
  inquiryId: _inquiryId,
  inquiryName: _inquiryName,
  userRole: _userRole,
  onView,
  onRefresh: _onRefresh,
}: InquiryManagementActionsProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState<'bottom' | 'top'>('bottom');
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (showMenu && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      // If less than 200px space below and more space above, open upward
      if (spaceBelow < 200 && spaceAbove > spaceBelow) {
        setMenuPosition('top');
      } else {
        setMenuPosition('bottom');
      }
    }
  }, [showMenu]);

  return (
    <div className="relative" ref={buttonRef}>
      <Button
        variant="ghost"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          setShowMenu(!showMenu);
        }}
        className="text-gray-600 hover:text-aalaw-red"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
        </svg>
      </Button>

      {showMenu && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setShowMenu(false)}
          />
          <div className={`absolute right-0 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-20 ${
            menuPosition === 'top' ? 'bottom-full mb-2' : 'mt-2'
          }`}>
            <div className="py-1">
              {/* View */}
              {onView && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onView();
                    setShowMenu(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  View Details
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
